import axios, { AxiosError } from "axios";
import { logHttpError } from "@/services/DatadogService/DatadogService";
import { sanitizePayload } from "@/utils/sanitizePayload";
import { isAuthenticationPage } from "@/internals/constants/paths";
import { stripLocaleFromPath } from "@/utils/locale";
import { ForceLogoutError } from "@/utils/apiErrorHandler";

const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
});

let isRedirectingToLogin = false;

// Interceptor for headers in stage environment
if (process.env.ENV === "stage" && typeof window === "undefined") {
  apiClient.interceptors.request.use((config) => {
    const headerName = process.env.SERVER_TEST_HEADER_NAME ?? "";
    const headerValue = process.env.SERVER_TEST_HEADER_VALUE ?? "";

    if (headerName && headerValue) {
      const headers: unknown = config.headers;
      if (
        headers &&
        typeof headers === "object" &&
        "set" in headers &&
        typeof (headers as { set?: unknown }).set === "function"
      ) {
        (headers as { set: (name: string, value: string) => void }).set(headerName, headerValue);
      } else {
        config.headers = { ...(config.headers ?? {}), [headerName]: headerValue };
      }
    } else {
      console.warn("WARN:: Server test headers are not set correctly");
    }
    return config;
  });
}

const getResponseMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== "object") return undefined;
  const message = (data as Record<string, unknown>).message;
  return typeof message === "string" ? message : undefined;
};

const parsePayload = (data: unknown): unknown => {
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

// Interceptor to capture HTTP errors and send them to Datadog
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const { config, response, request, code } = error;
    const timestamp = new Date().toISOString();

    if (response) {
      const { status, data } = response;

      logHttpError({
        message: getResponseMessage(data) || error.message || "API request failed",
        statusCode: status,
        endpoint: config?.url || "unknown",
        method: config?.method?.toUpperCase() || "UNKNOWN",
        payload: sanitizePayload(parsePayload(config?.data)),
        response: sanitizePayload(data),
        timestamp,
        userContext: {
          baseURL: config?.baseURL,
          headers: sanitizePayload(config?.headers),
        },
      });

      if (
        status === 401 &&
        typeof window !== "undefined" &&
        !isAuthenticationPage(stripLocaleFromPath(window.location.pathname))
      ) {
        if (!isRedirectingToLogin) {
          isRedirectingToLogin = true;
          window.dispatchEvent(new CustomEvent("force_logout"));

          setTimeout(() => {
            isRedirectingToLogin = false;
          }, 2000);
        }

        return Promise.reject(new ForceLogoutError());
      }
    } else if (request) {
      logHttpError({
        message: code === "ECONNABORTED" ? "Request timeout" : "Network error",
        statusCode: 0,
        endpoint: config?.url || "unknown",
        method: config?.method?.toUpperCase() || "UNKNOWN",
        payload: sanitizePayload(parsePayload(config?.data)),
        timestamp,
      });
    }

    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

export default apiClient;
